import * as React from "react";
import { useMappedState } from "redux-react-hook";
import { Formik } from "formik";
import * as _ from "lodash";

import { FieldMultiSelect } from "src/library/components/Forms";
import {
  ICustomer,
  useCustomerApi
} from "src/core/customers/api/customers.api";
import type { IStore } from "src/core/shared/store";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { useCurrentUser } from "src/core/users/hooks";

interface CustomerMultiSelect {
  onCustomersChange: (data: { selected: string, customersSelected: string[]; fullCustomersSelected: ICustomer[]; }) => void;
  disabledOptions?: number[];
  customerFilters?: FilterParams<ICustomer>;
  customerPagination?: PaginatedParams<ICustomer>,
  debounceTime?: number
  singleSelection?: boolean
  labelOverride?: string
}

const defaultPagination: PaginatedParams<ICustomer> = {
  limit: 100,
  offset: 0,
  page: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

export const CustomerMultiSelect: React.ReactFCWithChildren<CustomerMultiSelect> = ({
  onCustomersChange, disabledOptions, customerPagination, customerFilters, debounceTime, singleSelection, labelOverride
}) => {
  const [ recipientCustomerState, setRecipientCustomerState ] = React.useState<Array<{ text: string; value: string }>>([]);
  const [ currentRecipientType ] = React.useState<string | null>(null);
  const [ loadedCustomers, setLoadedCustomers ] = React.useState<boolean>(false);
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings && global.settings.terminology }));
  const [ customersResponse, getCustomers ] = useCustomerApi("LIST_CUSTOMERS");
  const [ { currentRole, currentUser } ] = useCurrentUser();
  const [ allCustomersSearched, setAllCustomersSearched ] = React.useState<ICustomer[]>([]);

  // On mount, get the customers
  React.useEffect(() => {
    getCustomers({
      pagination: defaultPagination,
      filters: currentRole?.name === "account-manager" ? { accountManagerId: currentUser?.id } : {}
    });
  }, [
    currentRole?.name,
    currentUser?.id,
    getCustomers
  ]);

  React.useEffect(() => {
    if (!loadedCustomers && (currentRecipientType === "branch" || currentRecipientType === "customer")) {
      getCustomers({
        pagination: customerPagination || defaultPagination,
        filters: customerFilters || {}
      });
    }
  }, [
    currentRecipientType,
    customerFilters,
    customerPagination,
    getCustomers,
    loadedCustomers
  ]);

  //for passing into the multiSelect
  const handleSpecificSearch = React.useCallback((search: string | null) => {
    if (search) {
      //get fuzzy searched drops
      getCustomers({
        pagination: {
          ...defaultPagination,
          limit: 50
        },
        //search for account number and name
        filters: {
          ...customerFilters,
          $or: [ { name: { $like: `%${search}%` } }, { accountNumber: { $like: `%${search}%` } } ]
        }
      });
    } else {
      getCustomers({
        pagination: customerPagination || defaultPagination,
        filters: customerFilters || {}
      });
    }
  }, [
    customerFilters,
    customerPagination,
    getCustomers
  ]);

  // Set list of customers as customer options.
  React.useEffect(() => {
    if (customersResponse.data && customersResponse.data.result) {
      const customers = customersResponse.data.result;

      const customerValues = customers.map(customer => ({
        text: `(${customer.accountNumber}) ${customer.name}`,
        value: customer.id.toString()
      }));

      //populate data with all the new data ensuring uniqueness
      setRecipientCustomerState(prev => {
        const merged = [ ...prev, ...customerValues ];

        //hash maps do not allow duplicate keys
        return Array.from(new Map(merged.map(option => [ option.value, option ])).values());
      });

      //keeping tabs on all the customers we have found
      setAllCustomersSearched(prev => {
        const merged = _.uniqBy([ ...prev, ...customers ], "id");

        return merged;
      });
      setLoadedCustomers(true);
    }
  }, [ customersResponse ]);

  const label = React.useMemo(() => {
    if (!singleSelection) {
      return `Select ${terminology ? terminology.customer.plural.text : "Customers"}`;
    } else {
      return `Select ${terminology ? terminology.customer.singular.text : "Customer"}`;
    }
  }, [ singleSelection, terminology ]);

  return (
    <Formik
      initialValues={{ recipientMultipleCustomers: undefined }}
      // dont submit as should be used in from above this
      onSubmit={() => null}
    >
      {({ values, handleChange }) => (
        <FieldMultiSelect
          values={values.recipientMultipleCustomers}
          name="recipientMultipleCustomers"
          handleChange={handleChange}
          label={labelOverride ? labelOverride : label}
          options={recipientCustomerState}
          disabledOptions={_.map(disabledOptions, d => d.toString())}
          validateOnChange={false}
          loading={customersResponse.pending}
          fieldLogic={(affected, oldSelected) => onCustomersChange({
            selected: affected,
            customersSelected: oldSelected,
            fullCustomersSelected: _.compact(_.map(oldSelected, selected => _.find(allCustomersSearched, c => c.id.toString() === selected)))
          })}
          handleSpecificSearch={handleSpecificSearch}
          debounceTime={debounceTime}
          singleSelection={singleSelection}
        />
      )}
    </Formik>
  );
};
